import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatError } from "@/lib/api";
import { ArrowLeft, Sparkles, Package, Trash2, Loader2, Info, Plus, Send, Lock } from "lucide-react";
import UploadingBadge from "@/components/common/UploadingBadge";
import { AnonymizeDialog } from "@/components/media/AnonymizeDialog";
import { TabBar } from "@/components/toggles";
import SubscriptionTiersPanel from "@/components/studio/SubscriptionTiersPanel";
import StudioSubscriberBenefits from "@/pages/StudioSubscriberBenefits";
import StudioMessagingConfigTab from "@/components/studio/StudioMessagingConfigTab";
// iter230 · Editor-Angleichung an Post-Composer (User-Vorgabe „bring the composer in line").
import RichTextEditor from "@/components/common/RichTextEditor";
import AIAssistBar from "@/components/ai/AIAssistBar";
import MonetizationModeSelector from "@/components/common/MonetizationModeSelector";
// iter238 · Publish-Consent-Checkbox (Variante B §5)
import PublishConsentCheckbox from "@/components/paid-media/PublishConsentCheckbox";
// iter244 · Analytics-Kachel im Studio-Dashboard
import StudioPaidMediaAnalytics from "@/components/paid-media/StudioPaidMediaAnalytics";

/**
 * StudioMonetizationPage — Batch 3 (iter79)
 *
 * Creator-Studio-Seite zum Verwalten von:
 * - SubscriptionPlans (Monatsabos, 80% Creator / 20% Plattform)
 * - PaidMediaAssets (Einzelkauf-Content, 80% / 20%)
 * - Payout-Übersicht
 *
 * Tabs: Abos · Paid Media · Auszahlungen
 */
export default function StudioMonetizationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // iter244 · Deep-Link ?tab=paid unterstützen (URL bleibt Source-of-Truth).
  const [params, setParams] = useSearchParams();
  const VALID_TABS = ["plans", "benefits", "paid", "messaging", "payouts"];
  const initialTab = VALID_TABS.includes(params.get("tab")) ? params.get("tab") : "plans";
  const [tab, setTab] = useState(initialTab);
  useEffect(() => {
    const t2 = params.get("tab");
    if (VALID_TABS.includes(t2) && t2 !== tab) setTab(t2);
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps
  const changeTab = (next) => {
    setTab(next);
    const p = new URLSearchParams(params);
    if (next === "plans") p.delete("tab"); else p.set("tab", next);
    setParams(p, { replace: true });
  };

  return (
    <div data-testid="studio-monetization" className="min-h-full">
      <div className="sticky top-0 z-40 bg-white px-4 py-3 border-b border-[#E5E5E5] flex items-center gap-3">
        <button onClick={() => navigate("/studio")} aria-label={t("studio_monet.back_aria")}>
          <ArrowLeft size={20} className="text-[#666]" />
        </button>
        <div>
          <p className="text-sm font-semibold text-[#0A0A0A]">{t("studio_monet.page_title")}</p>
          <p className="text-[10px] text-[#999]">{t("studio_monet.subtitle")}</p>
        </div>
      </div>

      <TabBar
        tabs={[
          { key: "plans", label: "Abos", testId: "studio-monetization-tab-plans" },
          { key: "benefits", label: "Abo-Vorteile", testId: "studio-monetization-tab-benefits" },
          { key: "paid", label: "Paid Media", testId: "studio-monetization-tab-paid" },
          { key: "messaging", label: "Nachrichten", testId: "studio-monetization-tab-messaging" },
          { key: "payouts", label: "Auszahlungen", testId: "studio-monetization-tab-payouts" },
        ]}
        activeKey={tab}
        onChange={changeTab}
      />

      {tab === "plans" && <PlansSection />}
      {tab === "benefits" && <StudioSubscriberBenefits />}
      {tab === "paid" && <PaidMediaSection />}
      {tab === "messaging" && <StudioMessagingConfigTab />}
      {tab === "payouts" && <PayoutsSection />}
    </div>
  );
}

function PlansSection() {
  const { t } = useTranslation();
  return (
    <div className="p-4 space-y-4">
      {/* iter123: 3-Tier-Modell (Standard/Premium/VIP) — Spec §1. */}
      <SubscriptionTiersPanel />

      <div className="p-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-2xl text-xs text-[#666]">
        <div className="flex items-start gap-2">
          <Info size={14} className="mt-0.5 shrink-0" />
          <p>{t("studio_monet.you_get_pre")} <strong>80&nbsp;%</strong>{t("studio_monet.platform_fee_suffix")} <strong>20&nbsp;%</strong>. Abonnenten erhalten Zugriff auf deinen gesamten Exklusiv-Content.</p>
        </div>
      </div>
    </div>
  );
}

function PaidMediaSection() {
  const { t } = useTranslation();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/studio/paid-media");
      setAssets(data.assets || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <Loader />;
  return (
    <div className="p-4 space-y-3">
      {/* iter244 · Analytics-Kachel (User-Wunsch) — nur wenn Käufe vorhanden. */}
      <StudioPaidMediaAnalytics />
      <div className="p-3 bg-[#FAFAFA] border border-[#E5E5E5] text-xs text-[#666]">
        <div className="flex items-start gap-2">
          <Info size={14} className="mt-0.5 shrink-0" />
          <p>Paid Media: einmaliger Kauf. Du bekommst <strong>80&nbsp;%</strong>{t("studio_monet.platform_fee_suffix")} <strong>20&nbsp;%</strong>. Video-Upload max 20 Minuten.</p>
        </div>
      </div>
      <button data-testid="studio-paid-new" onClick={() => setCreating(true)}
        className="w-full h-11 border border-dashed border-[#0A0A0A] text-sm font-medium flex items-center justify-center gap-2">
        <Plus size={16} /> Neuen Content erstellen
      </button>
      {creating && <PaidMediaForm onDone={() => { setCreating(false); load(); }} onCancel={() => setCreating(false)} />}
      {assets.map((a) => <PaidMediaCard key={a.asset_id} asset={a} onChange={load} />)}
      {assets.length === 0 && !creating && <p className="text-center text-sm text-[#999] py-8">Noch kein Paid Media</p>}
    </div>
  );
}

function PaidMediaForm({ onDone, onCancel }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [mediaType, setMediaType] = useState("IMAGE");
  const [adult, setAdult] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [anonPaidFile, setAnonPaidFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // iter230 · Modus-Auswahl (User-Vorgabe „Einzelkauf / Abo / PPV").
  const [mode, setMode] = useState("SINGLE_BUY");
  const [visibleTiers, setVisibleTiers] = useState([]);
  // iter238 (Variante B §5) · Publish-Consent-Pflicht.
  const [publishConsent, setPublishConsent] = useState(false);

  const uploadPaidBlob = async (blob, isVideo) => {
    setUploading(true);
    const fd = new FormData(); fd.append("file", blob, blob.name || (isVideo ? "media.mp4" : "media.jpg"));
    try {
      const { data } = await api.post("/upload?area_scope=SOCIAL", fd);
      setMediaUrl(`/api/files/${data.path}`);
      if (isVideo) setMediaType("VIDEO"); else setMediaType("IMAGE");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (formatError(err) || "Upload fehlgeschlagen"));
    } finally { setUploading(false); }
  };
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = "";
    setError("");
    const isVideo = (f.type || "").startsWith("video/");
    // iter173f · Bilder zuerst durch die Anonymisierungs-Engine; Videos direkt.
    if (isVideo) { await uploadPaidBlob(f, true); return; }
    setAnonPaidFile(f);
  };
  const submit = async () => {
    setError(""); setSubmitting(true);
    try {
      const priceNum = parseFloat(String(price).replace(",", ".")) || 0;
      await api.post("/studio/paid-media", {
        title: title.trim(),
        description: description.trim() || undefined,
        price_eur: priceNum,
        media_url: mediaUrl, media_type: mediaType, adult,
        monetization_mode: mode,
        visible_tiers: visibleTiers,
        // iter238 · Publish-Consent-Pflicht (Backend liefert sonst 422).
        publish_consent: publishConsent,
      });
      onDone();
    } catch (e) { setError(formatError(e) || "Fehler"); }
    finally { setSubmitting(false); }
  };
  // iter230 · Submit-Validierung spiegelt Backend-Regeln.
  const priceValid = mode === "SUBSCRIBERS_ONLY" ? true : parseFloat(String(price).replace(",", ".")) > 0;
  const tiersValid = mode === "SINGLE_BUY" ? true : visibleTiers.length > 0;
  // iter238 · Consent-Pflicht ebenfalls Frontend-Guard.
  const canSubmit = !submitting && title && mediaUrl && priceValid && tiersValid && publishConsent;
  return (
    <div data-testid="studio-paid-form" className="p-4 border border-[#0A0A0A] bg-white space-y-3">
      {error && <p className="text-xs text-red-500">{error}</p>}
      <input data-testid="studio-paid-title" value={title} onChange={e=>setTitle(e.target.value)} placeholder="Titel" className="w-full h-10 px-3 border border-[#E5E5E5] text-sm" />
      <RichTextEditor
        testId="studio-paid-desc-editor"
        value={description}
        onChange={setDescription}
        placeholder={t("monetization.desc_ph")}
        minHeight={100}
      />
      <AIAssistBar
        area="SOCIAL"
        value={description}
        onApply={setDescription}
        fieldType="description"
        testIdSuffix="studio-paid"
      />
      <MonetizationModeSelector
        mode={mode}
        onModeChange={setMode}
        tiers={visibleTiers}
        onTiersChange={setVisibleTiers}
        price={price}
        onPriceChange={(v) => setPrice(v === "" ? "" : String(v))}
        testIdPrefix="studio-paid-mode"
        allowedModes={["SINGLE_BUY", "SUBSCRIBERS_ONLY", "PPV"]}
      />
      {/* iter232 · Doppeltes Preis-Feld entfernt (Selector rendert eigenes). */}
      <label className="block w-full h-32 border border-dashed border-[#E5E5E5] flex items-center justify-center cursor-pointer text-xs text-[#999]">
        {mediaUrl ? (mediaType === "VIDEO" ? "Video hochgeladen ✓" : <img src={mediaUrl} alt="" className="max-h-full" />) : uploading ? <UploadingBadge active testid="studio-paid-uploading" size="md" /> : "Bild/Video auswählen"}
        <input data-testid="studio-paid-file" type="file" accept="image/*,video/*" className="hidden" onChange={upload} />
      </label>
      <label className="flex items-center gap-2 text-xs text-[#666]">
        <input type="checkbox" checked={adult} onChange={e=>setAdult(e.target.checked)} /> 18+ Inhalt
      </label>
      <PublishConsentCheckbox
        checked={publishConsent}
        onChange={setPublishConsent}
        testId="studio-paid-publish-consent"
      />
      <div className="flex gap-2 pt-2">
        <button onClick={onCancel} className="h-10 px-4 rounded-full border border-[#E5E5E5] text-sm text-[#666]">{t("studio_monet.cancel")}</button>
        <button data-testid="studio-paid-submit" onClick={submit} disabled={!canSubmit}
          className="flex-1 rounded-full h-10 bg-[#0A0A0A] text-white text-sm disabled:opacity-30">
          {submitting ? "Speichere..." : "Erstellen"}
        </button>
      </div>
      {anonPaidFile && (
        <AnonymizeDialog
          file={anonPaidFile}
          context="PAID_MEDIA"
          returnBlob
          onCancel={() => setAnonPaidFile(null)}
          onComplete={async (finalBlob) => { setAnonPaidFile(null); await uploadPaidBlob(finalBlob, false); }}
        />
      )}
    </div>
  );
}

function PaidMediaCard({ asset, onChange }) {
  const { t } = useTranslation();
  const publish = async () => { await api.post(`/studio/paid-media/${asset.asset_id}/publish`); onChange(); };
  const del = async () => {
    if (!window.confirm(t("monetization.remove_confirm"))) return;
    try { await api.delete(`/studio/paid-media/${asset.asset_id}`); onChange(); }
    catch (e) { window.alert(e?.response?.data?.detail?.message || "Löschen fehlgeschlagen"); }
  };
  const locked = !!asset.first_purchase_at
    || ["LOCKED_AFTER_FIRST_PURCHASE", "HARD_TAKEDOWN", "ARCHIVED"]
        .includes(asset.lifecycle_state);
  return (
    <div data-testid={`studio-paid-${asset.asset_id}`} className="p-3 border border-[#E5E5E5] bg-white">
      <div className="flex items-start gap-3">
        <div className="relative w-16 h-16 bg-[#F5F5F5] flex items-center justify-center overflow-hidden">
          {asset.media_type === "IMAGE" && asset.media_url ? <img src={asset.media_url} alt="" className="w-full h-full object-cover" /> : <Package size={20} className="text-[#CCC]" />}
          {locked && (
            <div
              data-testid={`studio-paid-lock-${asset.asset_id}`}
              className="absolute inset-0 flex items-center justify-center bg-black/40 text-white"
              title="Bildmaterial gesperrt seit erstem Kauf"
            >
              <Lock size={16} />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#0A0A0A] truncate">{asset.title}</p>
          <p className="text-sm font-bold text-[#0A0A0A]">{Number(asset.price_eur || 0).toFixed(2)} €</p>
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            <span className={`text-[10px] px-1.5 py-0.5 border ${asset.status === "PUBLISHED" ? "border-green-500 text-green-700 bg-green-50" : "border-amber-500 text-amber-700 bg-amber-50"}`}>{asset.status}</span>
            <span className="text-[10px] px-1.5 py-0.5 border border-[#E5E5E5] text-[#666]">{asset.media_type}</span>
            {asset.adult && <span className="text-[10px] px-1.5 py-0.5 border border-[#E5E5E5] text-[#666]">18+</span>}
            {locked && (
              <span className="text-[10px] px-1.5 py-0.5 border border-[#0A0A0A] bg-[#0A0A0A] text-white rounded-full">
                🔒 Bildmaterial gesperrt
              </span>
            )}
          </div>
          <p className="text-[10px] text-[#999] mt-1">
            {asset.purchase_count || 0} Käufe
            {asset.first_purchase_at && (
              <> · Erstkauf {new Date(asset.first_purchase_at).toLocaleDateString("de-DE")}</>
            )}
          </p>
        </div>
      </div>
      <div className="flex gap-2 mt-2">
        {asset.status === "DRAFT" && (
          <button data-testid={`paid-publish-${asset.asset_id}`} onClick={publish}
                  className="flex-1 h-9 rounded-full bg-[#0A0A0A] text-white text-xs flex items-center justify-center gap-1">
            <Send size={12} /> Veröffentlichen
          </button>
        )}
        {asset.lifecycle_state !== "ARCHIVED" && asset.lifecycle_state !== "HARD_TAKEDOWN" && (
          <button
            data-testid={`paid-delete-${asset.asset_id}`}
            onClick={del}
            className="h-9 px-3 rounded-full border border-[#E5E5E5] text-xs text-[#666] hover:bg-[#F5F5F5]"
          >
            Entfernen
          </button>
        )}
      </div>
    </div>
  );
}

function PayoutsSection() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/studio/payouts/summary"); setData(data); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <Loader />;
  const rows = data?.rows || [];
  const total = rows.reduce((s, r) => s + (r.payout_sum || 0), 0);
  return (
    <div className="p-4 space-y-3">
      <div className="p-4 bg-[#0A0A0A] text-white">
        <p className="text-xs text-white/70 uppercase tracking-wider">Gesamt-Auszahlung</p>
        <p className="text-3xl font-bold mt-1">{total.toFixed(2)} €</p>
      </div>
      <div className="p-3 bg-[#FAFAFA] border border-[#E5E5E5] text-xs text-[#666]">
        <p>Payout-Splits: Abos & Paid Media <strong>80/20</strong> · STX-Shop <strong>80/20</strong> {t("studio_monet.rescue_bags")} <strong>50/50</strong> · Marketplace <strong>100 % Seller</strong> {t("studio_monet.platform_earn_note")}</p>
      </div>
      {rows.length === 0 ? (
        <p className="text-center text-sm text-[#999] py-6">{t("studio_monet.no_payouts")}</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} data-testid={`payout-row-${i}`} className="p-3 border border-[#E5E5E5] bg-white flex justify-between">
              <div>
                <p className="text-xs font-medium text-[#0A0A0A]">{r._id?.source} · {r._id?.status}</p>
                <p className="text-[10px] text-[#999]">{r.count} Transaktionen · Gebühr {r.fee_sum.toFixed(2)} €</p>
              </div>
              <p className="text-sm font-bold text-[#0A0A0A]">{r.payout_sum.toFixed(2)} €</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Loader() {
  return (
    <div className="flex justify-center py-16">
      <Loader2 size={20} className="animate-spin text-[#999]" />
    </div>
  );
}
